'use client';
import { use, useEffect, useRef } from 'react';
import { useShouldShowHaveYouMetDialog } from '@/components/features/dialogs/HaveYouMetDialog';
import {
  DialogContext,
  DialogKey,
} from '@/components/features/dialogs/infra/context/DialogContext';
import { useShouldShowUserAvailabilityNudgeDialog } from '@/components/features/dialogs/UserAvailabilityNudgeDialog';
import {
  useShouldShowNotificationOptInDialog,
  type NotificationScenario,
} from '@/components/features/push-notifications/dialogs/NotificationOptInDialog';

interface DialogDescriptor {
  id: DialogKey;
  shouldShow: boolean;
  loading: boolean;
  getProps?: () => unknown | undefined;
}

export function DialogPriorityManager({
  notificationOptInDialogScenario,
  hasAvailability,
}: {
  notificationOptInDialogScenario: NotificationScenario;
  hasAvailability: boolean;
}) {
  const { openDialog, closeDialog, openDialogsCount } = use(DialogContext);
  const haveYouMet = useShouldShowHaveYouMetDialog();
  const notificationOptIn = useShouldShowNotificationOptInDialog(
    notificationOptInDialogScenario
  );
  const userAvailabilityNudge = useShouldShowUserAvailabilityNudgeDialog();

  const dialogDescriptors: DialogDescriptor[] = [
    {
      id: 'have-you-met',
      shouldShow: haveYouMet.shouldShow,
      loading: haveYouMet.loading,
      getProps: () => haveYouMet.props,
    },
    {
      id: 'notification-opt-in',
      shouldShow: notificationOptIn.shouldShow,
      loading: notificationOptIn.loading,
      getProps: () => ({ scenario: notificationOptInDialogScenario }),
    },
    {
      id: 'user-availability-nudge',
      shouldShow: !hasAvailability && userAvailabilityNudge.shouldShow,
      loading: userAvailabilityNudge.loading,
      getProps: () =>
        'props' in userAvailabilityNudge
          ? userAvailabilityNudge.props
          : undefined,
    },
  ];

  const hasShownAnyDialogRef = useRef(false);
  const anyLoading = dialogDescriptors.some((d) => d.loading);

  useEffect(() => {
    const allowToShowDialog =
      openDialogsCount === 0 && !anyLoading && !hasShownAnyDialogRef.current;

    for (const dialog of dialogDescriptors) {
      if (dialog.shouldShow) {
        if (allowToShowDialog) {
          const props = dialog.getProps ? dialog.getProps() : undefined;
          if (props !== undefined) {
            openDialog(dialog.id, props);
          } else {
            // If dialog expects props but none are provided, pass an empty object
            openDialog(dialog.id, {});
          }
          hasShownAnyDialogRef.current = true;
        } else if (openDialogsCount > 1) {
          // if more than the current dialog is open, we want to close it
          closeDialog(dialog.id);
        }
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ...dialogDescriptors.map((d) => d.shouldShow),
    anyLoading,
    openDialogsCount,
  ]);

  return null;
}

export default DialogPriorityManager;
